// src/shared/ui/chart/charts/programBudgetCharts.js
import { formatFiscalYearFromStart } from "../../../utils/fiscalYearUtils";

const formatFiscalYearFromEnd = (endYear) =>
  typeof endYear === "number" && Number.isFinite(endYear)
    ? formatFiscalYearFromStart(endYear - 1)
    : "";

/**
 * Build chart data for **Approved-only** time series.
 *
 * Each fieldDef becomes a separate series, and each series contains
 * one value per fiscal year (sorted ascending).
 *
 * - Labels are fiscal-year labels (e.g., "2023–2024").
 * - Values are taken directly from each `approvedRows` row using `fd.key`.
 * - Non-numeric or missing values are coerced to `0`.
 *
 * @param {Array<Object>} approvedRows
 *   Array of row objects produced by `buildProgramRowsFromDoc` for the
 *   "Approved" section. Each row is expected to contain:
 *   - `year` (number)
 *   - numeric properties keyed by fieldDef.key
 *
 * @param {Array<Object>} fieldDefs
 *   Program field definitions (PROGRAM_*_CONFIG.fieldDefs). Each fieldDef
 *   should include:
 *   - `key` (string)
 *   - `label` (string)
 *   - `valueType` (e.g. "currency" | "count")
 *   - `defaultVisible` (optional boolean)
 *   - `group` (optional string)
 *
 * @returns {{
 *   approvedChartLabels: string[],
 *   approvedChartSeries: Array<{
 *     key: string,
 *     label: string,
 *     data: number[],
 *     defaultVisible: boolean,
 *     valueType: string,
 *     group: string
 *   }>
 * }}
 *   Labels and series suitable for feeding into a chart component.
 */
export function buildApprovedChartData(approvedRows, proposedRows, fieldDefs) {
  const approved = Array.isArray(approvedRows) ? approvedRows : [];
  const proposed = Array.isArray(proposedRows) ? proposedRows : [];
  const defs = Array.isArray(fieldDefs) ? fieldDefs : [];

  const approvedByYear = new Map();
  for (const r of approved) {
    if (typeof r?.year === "number") approvedByYear.set(r.year, r);
  }

  const proposedByYear = new Map();
  for (const r of proposed) {
    if (typeof r?.year === "number") proposedByYear.set(r.year, r);
  }

  // Union of years so Proposed-only years show up on Approved charts
  const yearSet = new Set([...approvedByYear.keys(), ...proposedByYear.keys()]);
  const years = Array.from(yearSet).sort((a, b) => a - b);

  const labels = years.map((y) => formatFiscalYearFromEnd(y));

  const series = defs.map((fd) => {
    const approvedData = years.map((y) => {
      const row = approvedByYear.get(y);
      if (!row) return null;
      const v = row?.[fd.key];
      return typeof v === "number" && Number.isFinite(v) ? v : (v ?? null);
    });

    // Proposed ONLY when Approved is missing
    const proposedData = years.map((y) => {
      if (approvedByYear.has(y)) return null;
      const row = proposedByYear.get(y);
      if (!row) return null;
      const v = row?.[fd.key];
      return typeof v === "number" && Number.isFinite(v) ? v : (v ?? null);
    });

    const hasProposedOverlay = proposedData.some(
      (v) => v !== null && v !== undefined,
    );

    return {
      key: fd.key,
      label: fd.label,
      data: approvedData,
      // NEW: LineChartWithToggles will render this as a shaded overlay
      proposedData: hasProposedOverlay ? proposedData : null,
      valueType: fd.valueType || "currency",
      group: fd.group || "other",
      defaultVisible: !!fd.defaultVisible,
    };
  });

  return {
    approvedChartLabels: labels,
    approvedChartSeries: series,
  };
}

/**
 * Build chart data for **Actual-only** time series.
 *
 * Similar to {@link buildApprovedChartData}, but:
 * - Uses actual rows instead of approved rows.
 * - Series keys are suffixed with "Actual" (e.g. "salariesAdminActual")
 *   to avoid conflicts when used alongside Approved series.
 *
 * @param {Array<Object>} actualRows
 *   Array of row objects produced by `buildProgramRowsFromDoc`
 *   for the "Actual" section.
 *
 * @param {Array<Object>} fieldDefs
 *   Program field definitions (PROGRAM_*_CONFIG.fieldDefs).
 *
 * @returns {{
 *   actualChartLabels: string[],
 *   actualChartSeries: Array<{
 *     key: string,
 *     label: string,
 *     data: number[],
 *     defaultVisible: boolean,
 *     valueType: string,
 *     group: string
 *   }>
 * }}
 *   Labels and series for Actual-only charts.
 */
export function buildActualChartData(actualRows, fieldDefs) {
  if (!actualRows.length) {
    return { actualChartLabels: [], actualChartSeries: [] };
  }

  const sorted = [...actualRows].sort((a, b) => a.year - b.year);
  const labels = sorted.map((row) => formatFiscalYearFromEnd(row.year));

  const series = fieldDefs.map((fd) => ({
    key: `${fd.key}Actual`,
    label: fd.label,
    data: sorted.map((row) => Number(row[fd.key]) || 0),
    defaultVisible:
      typeof fd.defaultVisible === "boolean"
        ? fd.defaultVisible
        : fd.valueType === "currency",
    valueType: fd.valueType || "currency",
    group: fd.group || "other",
  }));

  return { actualChartLabels: labels, actualChartSeries: series };
}

/**
 * Build chart data for **Actual Spending vs Approved Budget paired** series.
 *
 * For each fieldDef, this creates a pair of series:
 *   - `${fieldKey}Approved` → "Label – Approved"
 *   - `${fieldKey}Actual`   → "Label – Actual"
 *
 * It also returns a `diffSeriesGroups` array that groups the Approved/Actual
 * pair under a single logical item, which is useful for legend controls.
 *
 * Only years for which both Approved and Actual rows exist are included.
 *
 * @param {Array<Object>} approvedRows
 *   Rows for "Approved" section (from `buildProgramRowsFromDoc`).
 *
 * @param {Array<Object>} actualRows
 *   Rows for "Actual" section.
 *
 * @param {Array<Object>} fieldDefs
 *   Field definitions that define which keys are charted.
 *
 * @returns {{
 *   diffChartLabels: string[],
 *   diffChartSeries: Array<{
 *     key: string,
 *     label: string,
 *     data: number[],
 *     defaultVisible: boolean,
 *     valueType: string,
 *     group: string
 *   }>,
 *   diffSeriesGroups: Array<{
 *     key: string,
 *     label: string,
 *     groupKey: string,
 *     seriesKeys: string[],
 *     defaultVisible: boolean
 *   }>
 * }}
 *   Labels, series, and logical groupings for paired Actual Spending vs Approved Budget charts.
 */
export function buildDiffChartData(approvedRows, actualRows, fieldDefs) {
  if (!approvedRows.length || !actualRows.length) {
    return { diffChartLabels: [], diffChartSeries: [], diffSeriesGroups: [] };
  }

  const approvedByYear = new Map(approvedRows.map((r) => [r.year, r]));
  const actualByYear = new Map(actualRows.map((r) => [r.year, r]));

  const commonYears = [...approvedByYear.keys()]
    .filter((y) => actualByYear.has(y))
    .sort((a, b) => a - b);

  if (!commonYears.length) {
    return { diffChartLabels: [], diffChartSeries: [], diffSeriesGroups: [] };
  }

  const labels = commonYears.map((year) => formatFiscalYearFromEnd(year));
  const series = [];
  const fieldKeyToPair = {};

  /**
   * Create approved/actual series for a single fieldDef and
   * store reference in fieldKeyToPair.
   *
   * @param {Object} fd
   */
  const makePairedSeries = (fd) => {
    const {
      key: fieldKey,
      label,
      valueType = "currency",
      group = "other",
    } = fd;

    const approvedData = commonYears.map((year) => {
      const row = approvedByYear.get(year);
      return row && typeof row[fieldKey] === "number" ? row[fieldKey] : 0;
    });

    const actualData = commonYears.map((year) => {
      const row = actualByYear.get(year);
      return row && typeof row[fieldKey] === "number" ? row[fieldKey] : 0;
    });

    const approvedSeries = {
      key: `${fieldKey}Approved`,
      label: `${label} – Approved`,
      data: approvedData,
      defaultVisible:
        typeof fd.defaultVisible === "boolean"
          ? fd.defaultVisible
          : valueType === "currency",
      valueType,
      group,
    };

    const actualSeries = {
      key: `${fieldKey}Actual`,
      label: `${label} – Actual`,
      data: actualData,
      defaultVisible:
        typeof fd.defaultVisible === "boolean"
          ? fd.defaultVisible
          : valueType === "currency",
      valueType,
      group,
    };

    series.push(approvedSeries, actualSeries);

    fieldKeyToPair[fieldKey] = {
      approvedKey: approvedSeries.key,
      actualKey: actualSeries.key,
      fieldDef: fd,
    };
  };

  fieldDefs.forEach(makePairedSeries);

  const groups = fieldDefs
    .map((fd) => {
      const pair = fieldKeyToPair[fd.key];
      if (!pair) return null;

      return {
        key: fd.key,
        label: fd.label,
        groupKey: fd.group || "other",
        seriesKeys: [pair.approvedKey, pair.actualKey],
        defaultVisible:
          typeof fd.defaultVisible === "boolean"
            ? fd.defaultVisible
            : fd.valueType === "currency",
      };
    })
    .filter(Boolean);

  return {
    diffChartLabels: labels,
    diffChartSeries: series,
    diffSeriesGroups: groups,
  };
}

/**
 * Detect salary/position pairs for **Average Salary** charts.
 *
 * Logic:
 * - Only considers fieldDefs where `valueType === "currency"` and `key`
 *   starts with `"salaries"`.
 * - Derives a matching positions key by stripping the `"salaries"` prefix
 *   and prepending `"positions"` (e.g., "salariesTeachers" → "positionsTeachers").
 * - Confirms that the positions field exists with `valueType === "count"`.
 * - Builds a user-friendly label by converting:
 *     "Salaries – X" → "Avg Salary – X"
 *     "Salaries X"   → "Avg Salary X"
 *   or else falls back to "Avg {originalLabel}".
 *
 * @param {Array<Object>} fieldDefs
 *   Full list of field definitions for the program.
 *
 * @returns {Array<{
 *   salaryKey: string,
 *   positionsKey: string,
 *   label: string
 * }>}
 *   Array of salary/position pair descriptors.
 */
export function buildSalaryPositionPairs(fieldDefs) {
  const DEBUG = true; // TEMP: remove or set false when done

  const defs = Array.isArray(fieldDefs) ? fieldDefs : [];
  const pairs = [];

  const salaryDefs = defs.filter(
    (fd) =>
      fd?.valueType === "currency" &&
      typeof fd?.key === "string" &&
      fd.key.startsWith("salaries"),
  );

  if (DEBUG) {
    console.debug("[AvgSalary] buildSalaryPositionPairs()", {
      fieldDefsLen: defs.length,
      salaryDefsLen: salaryDefs.length,
      salaryKeys: salaryDefs.map((d) => d.key),
    });
  }

  salaryDefs.forEach((fd) => {
    const suffix = fd.key.slice("salaries".length);
    const posKey = `positions${suffix}`;

    const posField = defs.find(
      (f) => f.key === posKey && f.valueType === "count",
    );

    if (!posField) {
      if (DEBUG) {
        const foundAny = defs.find((f) => f.key === posKey) || null;
        console.warn(
          "[AvgSalary] No matching positions fieldDef for salary key",
          {
            salaryKey: fd.key,
            expectedPositionsKey: posKey,
            expectedPositionsValueType: "count",
            foundPositionsFieldDef: foundAny,
          },
        );
      }
      return;
    }

    // --- Label: just the role (e.g., "Teachers"), no "Avg Salary –" prefix ---
    let label = fd.label || suffix || fd.key;

    // If the fieldDef label is like "Salaries – Teachers", keep only "Teachers"
    if (typeof label === "string") {
      label = label.trim();

      // Handle both "Salaries – X" and "Salaries - X"
      label = label.replace(/^Salaries\s*[–-]\s*/i, "");

      // Handle "Salaries X" (space style)
      label = label.replace(/^Salaries\s+/i, "");

      label = label.trim();
    }

    // Final fallback: suffix (e.g. "Teachers") if label is empty
    if (!label) label = suffix || fd.key;

    pairs.push({
      salaryKey: fd.key,
      positionsKey: posKey,
      label, // <-- now just "Teachers"
    });
  });

  if (DEBUG) {
    console.debug("[AvgSalary] Pairs built", {
      pairsLen: pairs.length,
      pairs,
    });

    if (salaryDefs.length > 0 && pairs.length === 0) {
      console.warn(
        "[AvgSalary] Found salary fields but built ZERO pairs. Likely: positions keys missing or valueType not 'count'.",
      );
    }
  }

  return pairs;
}

/**
 * Build chart data for **Average Salary** by field.
 *
 * Uses:
 *   avg = salaryTotal / positionsCount
 *
 * for each year and each salary/position pair.
 *
 * Notes:
 * - Only Actual rows are used (Approved values are ignored here).
 * - Division by zero or missing positions results in `null` for that point.
 *
 * @param {Array<Object>} approvedRows
 *   Currently ignored, kept only for signature compatibility with other
 *   chart builders. Can be passed as `[]`.
 *
 * @param {Array<Object>} actualRows
 *   Rows for "Actual" section (from `buildProgramRowsFromDoc`).
 *
 * @param {Array<{
 *   salaryKey: string,
 *   positionsKey: string,
 *   label: string
 * }>} salaryPositionPairs
 *   Output of {@link buildSalaryPositionPairs}.
 *
 * @returns {{
 *   avgSalaryLabels: string[],
 *   avgSalarySeries: Array<{
 *     key: string,
 *     label: string,
 *     data: Array<number|null>,
 *     defaultVisible: boolean,
 *     valueType: string,
 *     group: string
 *   }>
 * }}
 *   Labels and series for average-salary charts.
 */
export function buildAvgSalaryChartData(
  approvedRows,
  actualRows,
  salaryPositionPairs,
  proposedRows = [],
) {
  const DEBUG = false; // TEMP: remove or set false when done

  const approved = Array.isArray(approvedRows) ? approvedRows : [];
  const actual = Array.isArray(actualRows) ? actualRows : [];
  const proposed = Array.isArray(proposedRows) ? proposedRows : [];
  const pairs = Array.isArray(salaryPositionPairs) ? salaryPositionPairs : [];

  if (!pairs.length) {
    if (DEBUG) {
      console.warn("[AvgSalary] No chart output (no pairs).", {
        pairsLen: pairs.length,
        approvedRowsLen: approved.length,
        actualRowsLen: actual.length,
        proposedRowsLen: proposed.length,
      });
    }
    return { avgSalaryLabels: [], avgSalarySeries: [] };
  }

  const approvedByYear = new Map(approved.map((r) => [r.year, r]));
  const actualByYear = new Map(actual.map((r) => [r.year, r]));
  const proposedByYear = new Map(proposed.map((r) => [r.year, r]));

  // Union of years across all sources (so chart can exist even if Actual is missing)
  const yearSet = new Set([
    ...approvedByYear.keys(),
    ...actualByYear.keys(),
    ...proposedByYear.keys(),
  ]);

  const years = [...yearSet]
    .filter((y) => typeof y === "number" && Number.isFinite(y))
    .sort((a, b) => a - b);

  if (!years.length) {
    if (DEBUG) console.warn("[AvgSalary] No chart output (no years).");
    return { avgSalaryLabels: [], avgSalarySeries: [] };
  }

  const labels = years.map((y) => formatFiscalYearFromEnd(y));

  const toFiniteNumberOrNull = (val) => {
    if (val == null) return null;
    const n = typeof val === "number" ? val : Number(val);
    return Number.isFinite(n) ? n : null;
  };

  // Prefer Actual row; fall back to Approved; then Proposed
  const pickRowForYear = (year) =>
    actualByYear.get(year) ||
    approvedByYear.get(year) ||
    proposedByYear.get(year);

  if (DEBUG) {
    console.debug(
      "[AvgSalary] Building chart (Actual preferred, fallback Approved/Proposed)",
      {
        years,
        pairsLen: pairs.length,
        hasAnyActual: actual.length > 0,
        hasAnyApproved: approved.length > 0,
        hasAnyProposed: proposed.length > 0,
      },
    );
  }

  const series = pairs.map((pair) => {
    const data = years.map((year) => {
      const row = pickRowForYear(year);
      if (!row) return null;

      const total = toFiniteNumberOrNull(row?.[pair.salaryKey]);
      const positions = toFiniteNumberOrNull(row?.[pair.positionsKey]);

      if (total == null || positions == null) return null;
      if (positions <= 0) return null;

      return total / positions;
    });

    if (DEBUG) {
      const nullPoints = data.filter((v) => v == null).length;
      console.debug("[AvgSalary] Series computed", {
        label: pair.label,
        salaryKey: pair.salaryKey,
        positionsKey: pair.positionsKey,
        points: data.length,
        nullPoints,
      });

      if (nullPoints === data.length) {
        const sampleYear = years[0];
        const sampleRow = pickRowForYear(sampleYear);
        console.warn("[AvgSalary] Series has no drawable points (all null).", {
          label: pair.label,
          salaryKey: pair.salaryKey,
          positionsKey: pair.positionsKey,
          sampleYear,
          sampleRowSalaryRaw: sampleRow?.[pair.salaryKey],
          sampleRowPositionsRaw: sampleRow?.[pair.positionsKey],
        });
      }
    }

    return {
      key: `avg_${pair.salaryKey}`,
      label: pair.label,
      data,
      defaultVisible: false,
      valueType: "currency",
      group: "salaries",
    };
  });

  return { avgSalaryLabels: labels, avgSalarySeries: series };
}

/**
 * Build **Budget vs Inflation** chart data.
 *
 * This chart compares:
 *   - Approved totals by year
 *   - Actual totals by year
 *   - An "inflation baseline" series that starts from the first Approved
 *     total and compounds using yearly CPI (or similar) percentages.
 *
 * Steps:
 * 1. Find years where both Approved and Actual data exist.
 * 2. Compute a total per year by summing selected currency fields.
 *    - If `inflationSumFields` is provided, those keys are summed.
 *    - Otherwise, all fieldDefs with `valueType === "currency"` are summed.
 * 3. Build an inflation baseline:
 *    - Start at Approved total for first common year.
 *    - For each subsequent year:
 *          baseline[i] = baseline[i-1] * (1 + inflationPct(prevYear)/100)
 *
 * @param {Array<Object>} approvedRows
 *   Rows for "Approved" section.
 *
 * @param {Array<Object>} actualRows
 *   Rows for "Actual" section.
 *
 * @param {Record<string, number>} inflationByYear
 *   Map of year (as string) → inflation percentage for that year
 *   (e.g. `{ "2022": 6.5, "2023": 3.1 }`).
 *
 * @param {Array<Object>} fieldDefs
 *   Program field definitions used to derive default sum keys.
 *
 * @param {string[]|null} inflationSumFields
 *   Optional explicit list of field keys to sum for totals. If null/empty,
 *   all currency-type fieldDefs are included.
 *
 * @returns {{
 *   inflationLabels: string[],
 *   approvedInflationSeries: number[],
 *   actualInflationSeries: number[],
 *   inflationBaselineSeries: number[]
 * }}
 *   Labels and three aligned numeric series for inflation comparison charts.
 */
export function buildInflationChartData(
  approvedRows,
  actualRows,
  proposedRowsOrInflationByYear,
  inflationByYearOrFieldDefs,
  fieldDefsOrInflationSumFields,
  inflationSumFieldsMaybe,
) {
  /**
   * Backward-compatible argument normalization.
   *
   * Old signature:
   *   (approvedRows, actualRows, inflationByYear, fieldDefs, inflationSumFields)
   *
   * New signature:
   *   (approvedRows, actualRows, proposedRows, inflationByYear, fieldDefs, inflationSumFields)
   */
  const hasNewSignature = Array.isArray(proposedRowsOrInflationByYear);

  const proposedRows = hasNewSignature ? proposedRowsOrInflationByYear : [];

  const inflationByYear = hasNewSignature
    ? inflationByYearOrFieldDefs || {}
    : proposedRowsOrInflationByYear || {};

  const fieldDefs = hasNewSignature
    ? fieldDefsOrInflationSumFields || []
    : inflationByYearOrFieldDefs || [];

  const inflationSumFields = hasNewSignature
    ? inflationSumFieldsMaybe
    : fieldDefsOrInflationSumFields;

  const approved = Array.isArray(approvedRows) ? approvedRows : [];
  const actual = Array.isArray(actualRows) ? actualRows : [];
  const proposed = Array.isArray(proposedRows) ? proposedRows : [];
  const defs = Array.isArray(fieldDefs) ? fieldDefs : [];

  // If we have literally nothing, bail cleanly.
  if (!approved.length && !actual.length && !proposed.length) {
    return {
      inflationLabels: [],
      approvedInflationSeries: [],
      actualInflationSeries: [],
      inflationBaselineSeries: [],
    };
  }

  const approvedByYear = new Map(approved.map((r) => [r.year, r]));
  const actualByYear = new Map(actual.map((r) => [r.year, r]));
  const proposedByYear = new Map(proposed.map((r) => [r.year, r]));

  // Union of years across approved + proposed + actual (so we extend into proposed-only years).
  const yearSet = new Set([
    ...approvedByYear.keys(),
    ...proposedByYear.keys(),
    ...actualByYear.keys(),
  ]);

  const years = [...yearSet]
    .filter((y) => typeof y === "number" && Number.isFinite(y))
    .sort((a, b) => a - b);

  if (!years.length) {
    return {
      inflationLabels: [],
      approvedInflationSeries: [],
      actualInflationSeries: [],
      inflationBaselineSeries: [],
    };
  }

  const labels = years.map((year) => formatFiscalYearFromEnd(year));

  const sumKeys =
    inflationSumFields &&
    Array.isArray(inflationSumFields) &&
    inflationSumFields.length
      ? inflationSumFields
      : defs.filter((fd) => fd.valueType === "currency").map((fd) => fd.key);

  const sumRow = (row) => {
    if (!row) return null;
    let saw = false;
    const total = sumKeys.reduce((acc, key) => {
      const v = row?.[key];
      if (typeof v === "number" && Number.isFinite(v)) {
        saw = true;
        return acc + v;
      }
      // Preserve existing behavior for string-ish numeric values (rare, but safe):
      const n = Number(v);
      if (v !== null && v !== undefined && Number.isFinite(n)) {
        saw = true;
        return acc + n;
      }
      return acc;
    }, 0);

    return saw ? total : null;
  };

  // Approved totals: Approved preferred; Proposed fallback when Approved missing
  const approvedTotals = years.map((year) =>
    sumRow(approvedByYear.get(year) || proposedByYear.get(year)),
  );

  // Actual totals: only actual; otherwise null (don’t force to 0)
  const actualTotals = years.map((year) => sumRow(actualByYear.get(year)));

  const buildBaselineFrom = (baseSeries) => {
    const infl = Array(years.length).fill(null);

    const baseIdx = baseSeries.findIndex(
      (v) => typeof v === "number" && Number.isFinite(v),
    );
    if (baseIdx < 0) return infl;

    infl[baseIdx] = baseSeries[baseIdx];

    for (let i = baseIdx + 1; i < years.length; i++) {
      const prevYear = years[i - 1]; // FY start year for previous point
      const prevVal = infl[i - 1];

      if (typeof prevVal !== "number" || !Number.isFinite(prevVal)) {
        infl[i] = null;
        continue;
      }

      // Apply inflation for the year we are leaving (prevYear)
      const pctRaw = inflationByYear?.[String(prevYear)];
      const pct =
        typeof pctRaw === "number" && Number.isFinite(pctRaw)
          ? pctRaw
          : Number(pctRaw);

      const factor = Number.isFinite(pct) ? 1 + pct / 100 : 1;
      infl[i] = prevVal * factor;
    }

    return infl;
  };

  // Baseline that matches Approved view start (Approved/Proposed totals)
  const inflationBaselineForApproved = buildBaselineFrom(approvedTotals);

  // Baseline that matches Actual view start (Actual totals)
  const inflationBaselineForActual = buildBaselineFrom(actualTotals);

  return {
    inflationLabels: labels,
    approvedInflationSeries: approvedTotals,
    actualInflationSeries: actualTotals,

    // Keep existing field for backward compatibility (Approved baseline)
    inflationBaselineSeries: inflationBaselineForApproved,

    // New optional field (Actual baseline)
    inflationBaselineSeriesForActual: inflationBaselineForActual,
  };
}
